import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Tab } from '@/types/menu'

/**
 * 最大标签数量限制（防止内存占用过大）
 */
const MAX_TABS = 10

interface TabbarState {
  /** 已打开的标签列表 */
  tabs: Tab[]
  /** 当前激活的标签ID（使用路径作为ID） */
  activeTab: string

  /** 添加新标签 */
  addTab: (tab: Tab) => void
  /** 移除标签 */
  removeTab: (id: string) => void
  /** 关闭其他标签（保留首页和当前标签） */
  removeOtherTabs: (id: string) => void
  /** 关闭左侧所有可关闭的标签 */
  removeLeftSide: (id: string) => void
  /** 关闭右侧所有可关闭的标签 */
  removeRightSide: (id: string) => void
  /** 设置激活标签 */
  setActiveTab: (id: string) => void
  /** 按条件过滤标签（不满足条件的可关闭标签将被移除） */
  filterTabs: (predicate: (tab: Tab) => boolean) => void
  /** 重置所有标签（仅保留首页） */
  reset: () => void
}

/**
 * 首页标签（默认）
 */
const HOME_TAB: Tab = {
  id: '/dashboard',
  title: '工作台首页',
  href: '/dashboard',
  icon: null as any, // 图标将在组件中动态获取
  closable: false,
  timestamp: Date.now(),
}

export const useTabbarStore = create<TabbarState>()(
  persist(
    (set, get) => ({
      // 初始状态：仅包含首页
      tabs: [HOME_TAB],
      activeTab: '/dashboard',

      // 添加标签
      addTab: (tab) =>
        set((state) => {
          // 检查标签是否已存在
          const exists = state.tabs.some((t) => t.id === tab.id)
          if (exists) {
            // 如果已存在，只更新激活状态
            return { activeTab: tab.id }
          }

          // 检查是否超过最大数量
          const tabs = [...state.tabs]
          if (tabs.length >= MAX_TABS) {
            // 移除最旧的可关闭标签（LRU策略）
            const closableIndex = tabs.findIndex((t) => t.closable)
            if (closableIndex !== -1) {
              tabs.splice(closableIndex, 1)
            }
          }

          // 添加新标签
          tabs.push({ ...tab, timestamp: Date.now() })

          return {
            tabs,
            activeTab: tab.id,
          }
        }),

      // 移除标签
      removeTab: (id) =>
        set((state) => {
          // 首页不可关闭
          const tab = state.tabs.find((t) => t.id === id)
          if (!tab || !tab.closable) return state

          const index = state.tabs.findIndex((t) => t.id === id)
          const newTabs = state.tabs.filter((t) => t.id !== id)

          // 如果关闭的是当前激活标签，需要激活相邻标签
          let newActiveTab = state.activeTab
          if (id === state.activeTab) {
            // 优先激活右侧标签，否则激活左侧标签
            const nextTab = state.tabs[index + 1] || state.tabs[index - 1]
            newActiveTab = nextTab?.id || '/dashboard'
          }

          return {
            tabs: newTabs,
            activeTab: newActiveTab,
          }
        }),

      // 关闭其他标签
      removeOtherTabs: (id) =>
        set((state) => {
          const tabs = state.tabs.filter((t) => t.id === id || !t.closable)
          return { tabs, activeTab: id }
        }),

      // 关闭左侧标签
      removeLeftSide: (id) =>
        set((state) => {
          const index = state.tabs.findIndex((t) => t.id === id)
          if (index === -1) return state

          const tabs = [
            ...state.tabs.slice(0, index).filter((t) => !t.closable),
            ...state.tabs.slice(index),
          ]

          return { tabs }
        }),

      // 关闭右侧标签
      removeRightSide: (id) =>
        set((state) => {
          const index = state.tabs.findIndex((t) => t.id === id)
          if (index === -1) return state

          const tabs = [
            ...state.tabs.slice(0, index + 1),
            ...state.tabs.slice(index + 1).filter((t) => !t.closable),
          ]

          return { tabs }
        }),

      // 设置激活标签
      setActiveTab: (id) => set({ activeTab: id }),

      // 按条件过滤标签
      filterTabs: (predicate) =>
        set((state) => {
          const filtered = state.tabs.filter((t) => !t.closable || predicate(t))
          const activeStillExists = filtered.some((t) => t.id === state.activeTab)
          return {
            tabs: filtered,
            activeTab: activeStillExists ? state.activeTab : '/dashboard',
          }
        }),

      // 重置
      reset: () => set({ tabs: [HOME_TAB], activeTab: '/dashboard' }),
    }),
    {
      name: 'tabbar-storage', // localStorage key
      partialize: (state) => ({
        tabs: state.tabs.map(tab => ({
          ...tab,
          icon: undefined, // 不持久化icon，避免序列化错误
        })),
        activeTab: state.activeTab,
      }),
    }
  )
)
